import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  // Fetch with a one-time auto-retry on mobile-flaky networks.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/templates", { timeout: 15000 });
        if (!cancelled) setItems(data?.templates || []);
        return (data?.templates || []).length > 0;
      } catch {
        return false;
      }
    };
    (async () => {
      const ok = await fetchOnce();
      if (!ok && !cancelled) {
        // Auto-retry once after 1.5s — mobile networks sometimes drop the first request
        await new Promise(r => setTimeout(r, 1500));
        if (!cancelled) await fetchOnce();
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const copyTemplate = async (body) => {
    await navigator.clipboard.writeText(body);
    toast.success("Template copied to clipboard.");
  };

  return (
    <div style={{ maxWidth: 1000, margin: "30px auto", padding: "0 24px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>FREE TEMPLATES</div>
        <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: 0, fontWeight: 400 }}>Obituary Templates</h1>
        <p className="muted" style={{ marginTop: 8 }}>Tap any template to view and copy. Use them as a starting point — or let our AI write a custom draft.</p>
      </div>

      {loading && (
        <div data-testid="templates-loading" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-card" style={{ padding: 22, opacity: 0.5 }}>
              <div style={{ height: 28, width: "60%", background: "#1f1f1f", borderRadius: 4, marginBottom: 12 }} />
              <div style={{ height: 14, background: "#171717", borderRadius: 4, marginBottom: 8 }} />
              <div style={{ height: 14, width: "82%", background: "#171717", borderRadius: 4, marginBottom: 8 }} />
              <div style={{ height: 14, width: "60%", background: "#171717", borderRadius: 4, marginBottom: 18 }} />
              <div style={{ height: 32, width: 90, background: "#1f1f1f", borderRadius: 6 }} />
            </div>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div data-testid="templates-empty" className="bg-card" style={{ padding: 36, textAlign: "center" }}>
          <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>Templates didn&apos;t load</div>
          <p className="muted" style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
            This page works best on desktop, but it should load on phones too. If you&apos;re seeing this on mobile, please refresh the page — or tap the button below to create a beautiful personalized obituary instead.
          </p>
          <Link to="/new" data-testid="templates-empty-cta" className="btn-gold" style={{ display: "inline-block", padding: "12px 22px", textDecoration: "none" }}>
            ✦ Create a Personalized Obituary
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {items.map(t => (
            <div key={t.id} data-testid={`template-${t.id}`} className="bg-card" style={{ padding: 22 }}>
              <div className="serif" style={{ fontSize: 24, color: "var(--gold)", marginBottom: 8 }}>{t.title}</div>
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 14, minHeight: 70 }}>{t.preview}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setOpen(t)} className="btn-ghost" style={{ fontSize: 13 }}>View</button>
                <button onClick={() => copyTemplate(t.body)} className="btn-gold" style={{ fontSize: 13 }}>Copy</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div onClick={() => setOpen(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} className="bg-card" style={{ padding: 32, maxWidth: 700, width: "100%", maxHeight: "85vh", overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div className="serif" style={{ fontSize: 28, color: "var(--gold)" }}>{open.title}</div>
              <button onClick={() => setOpen(null)} className="btn-ghost" style={{ fontSize: 13 }}>Close</button>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Cormorant Garamond, serif", fontSize: 17, lineHeight: 1.85, color: "#e6e6e6" }}>{open.body}</pre>
            <div style={{ marginTop: 20 }}>
              <button onClick={() => copyTemplate(open.body)} className="btn-gold">Copy Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
